import React from "react"
import {Row, Col, Switch, Checkbox} from "antd"
import {cloneDeep, isEqual, keyBy} from "lodash"
import {BasePopup} from "components/popup"
import {BaseInput, BasePhoneInput, BaseTextArea} from "components/inputs"
import {userPersonalContactsServiceV3} from "services"
import {KlassDropdown} from "components/Select"
import {BaseButton} from "components/buttons"
import styles from "./AddContactForm.module.css"
import {withTranslation} from "react-i18next"
import {validateEmail} from "helpers/validation"
import {getValuesChangeTwoObj, handleError, toastError, toastSuccess} from "helpers"
import {FormLabel} from "components/Form"

type Props = {
    isShow: boolean
    onClose: Function
    data?: any
    userId?: number
    t: Function
    personalContactRoles: any
}

type State = {
    errors: any
    newData: any
    oldData: any
    isSubmitting: boolean
    showDefault: boolean
}

const disableDefaultByRole = ["Other", "Family"]
const DEFAULT_DATA = {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    message: "",
    userPersonalContactRoleId: null,
    isEmergency: false,
    isDefault: false
}
class AddContactForm extends React.Component<Props, State> {
    constructor(props) {
        super(props)
        this.state = {
            newData: cloneDeep(DEFAULT_DATA),
            isSubmitting: false,
            errors: {},
            showDefault: true,
            oldData: cloneDeep(DEFAULT_DATA)
        }
    }

    componentDidUpdate(prevProps) {
        const {data} = this.props
        const {contactProfile, isDefault, isEmergency, userPersonalContactRoleId, message, id} = data
        if (!isEqual(prevProps.data, this.props.data)) {
            this.setState({
                newData: id
                    ? cloneDeep({...contactProfile, isDefault, isEmergency, userPersonalContactRoleId, id, message})
                    : cloneDeep(DEFAULT_DATA),
                oldData: id
                    ? cloneDeep({...contactProfile, isDefault, isEmergency, userPersonalContactRoleId, id, message})
                    : cloneDeep(DEFAULT_DATA)
            })
        }
    }

    onChangeData = (key, value) => {
        const {newData, errors} = this.state
        const {personalContactRoles} = this.props
        let showDefaultByRole = true
        delete errors[key]
        if ("userPersonalContactRoleId" === key) {
            const roleName = personalContactRoles.find((role) => role.id === value)
            showDefaultByRole = !disableDefaultByRole.includes(roleName.name)
        }
        newData[key] = value
        this.setState({newData, showDefault: showDefaultByRole})
    }

    validateForm = () => {
        const {newData, errors} = this.state
        const {t} = this.props
        const newErrors = {...errors}
        const ids = Object.keys(newData)
        ids.forEach((id) => {
            if (!newData[id] && !["isEmergency", "isDefault", "address", "phone", "message"].includes(id)) {
                newErrors[id] = "Field is required"
            } else {
                delete newErrors[id]
            }
        })
        if (newData.email && !validateEmail(newData.email)) {
            toastError(t("user:contact.validEmail"))
            return false
        }
        if (Object.keys(newErrors).length) {
            toastError(t("common:message.completeForm"))
            this.setState({errors: newErrors})
            return false
        }
        return true
    }

    onSubmit = async () => {
        const {newData} = this.state
        const {userId, t} = this.props
        const isInvalid = this.validateForm()
        if (!isInvalid) {
            return
        }
        this.setState({isSubmitting: true})
        try {
            const sendData = {
                ...newData,
                userId: userId,
                isEmergency: newData.isEmergency ? 1 : 0,
                isDefault: newData.isDefault ? 1 : 0
            }
            await userPersonalContactsServiceV3.invite(sendData)
            this.props.onClose()
            this.setState({
                newData: cloneDeep(DEFAULT_DATA),
                oldData: cloneDeep(DEFAULT_DATA)
            })
            toastSuccess(t("user:contact.addContactSuccess"))
        } catch (e) {
            handleError(e)
        } finally {
            this.setState({isSubmitting: false})
        }
    }
    getDataSubmit = () => {
        const {oldData, newData} = this.state
        const submitData: any = getValuesChangeTwoObj(oldData, newData)
        if (typeof submitData?.isEmergency === "boolean") {
            submitData.isEmergency = submitData.isEmergency ? 1 : 0
        }
        if (typeof submitData?.isDefault === "boolean") {
            submitData.isDefault = submitData.isDefault ? 1 : 0
        }
        return submitData
    }

    onEdit = async () => {
        const {data, t} = this.props
        const isInvalid = this.validateForm()
        if (!isInvalid) {
            return
        }
        try {
            this.setState({isSubmitting: true})
            const submitData = this.getDataSubmit()
            await userPersonalContactsServiceV3.update(data.id, submitData)
            toastSuccess(t("common:message.updateSuccess"))
            this.props.onClose()
        } catch (e) {
            toastError(t("common:message.updateFailure"))
        } finally {
            this.setState({
                newData: cloneDeep(DEFAULT_DATA),
                oldData: cloneDeep(DEFAULT_DATA)
            })
            this.setState({isSubmitting: false})
        }
    }

    onClickCancelBtn = () => {}

    render() {
        const {newData, isSubmitting, errors, showDefault} = this.state
        const {isShow, onClose, t, personalContactRoles} = this.props
        const roles = personalContactRoles
        const rolesKeyById = keyBy(roles, "id")
        const roleValue = rolesKeyById[newData.userPersonalContactRoleId] || null
        const isChanged = this.getDataSubmit()

        return (
            <BasePopup isShow={isShow} onClose={onClose} leftIcon="PERSON_FILL" leftIconColor="#fff" width="880px">
                <p className={styles.title}>{newData.id ? t("contact.detail") : t("user.contact")}</p>
                <div className={styles.bodyWrap}>
                    <Row>
                        <Col span={12}>
                            <div className={styles.bodyItemName}>
                                <FormLabel label={t("user.firstName")} isRequired />
                                <BaseInput
                                    onChange={(newValue) => this.onChangeData("firstName", newValue)}
                                    placeholder={t("user.firstName")}
                                    value={newData.firstName}
                                    className={!!errors["firstName"] ? styles.error : ""}
                                />
                            </div>
                        </Col>
                        <Col span={12}>
                            <div className={styles.bodyItem}>
                                <FormLabel label={t("user.lastName")} isRequired />
                                <BaseInput
                                    onChange={(newValue) => this.onChangeData("lastName", newValue)}
                                    placeholder={t("user.lastName")}
                                    value={newData.lastName}
                                    className={!!errors["lastName"] ? styles.error : ""}
                                />
                            </div>
                        </Col>
                    </Row>
                    <div className={styles.bodyItem}>
                        <FormLabel label={t("user.email")} isRequired />
                        <BaseInput
                            onChange={(newValue) => this.onChangeData("email", newValue)}
                            placeholder={t("user.email")}
                            value={newData.email}
                            className={!!errors["email"] ? styles.error : ""}
                        />
                    </div>
                    <div className={styles.bodyItem}>
                        <p className={styles.bodyItemTitle}>{t("user.address")}</p>
                        <BaseInput
                            onChange={(newValue) => this.onChangeData("address", newValue)}
                            placeholder={t("user.address")}
                            value={newData.address}
                            className={!!errors["address"] ? styles.error : ""}
                        />
                    </div>
                    <div className={styles.bodyItem}>
                        <p className={styles.bodyItemTitle}>{t("user.phone")}</p>
                        <BasePhoneInput
                            onChange={(newValue) => this.onChangeData("phone", newValue)}
                            value={newData.phone}
                            isError={!!errors["phone"]}
                        />
                    </div>
                    <div className={styles.bodyItem}>
                        <p className={styles.bodyItemTitle}>{t("user.notes")}</p>
                        <BaseTextArea
                            onChange={(newValue) => this.onChangeData("message", newValue)}
                            placeholder={t("user.notes")}
                            value={newData.message}
                            className={!!errors["message"] ? styles.error : ""}
                        />
                    </div>
                </div>
                <Row>
                    <Col span={12}>
                        <div>
                            <FormLabel
                                label={t("user:contact.relationship")}
                                className={styles.bodyItemTitle}
                                isRequired
                            />
                            <Checkbox
                                checked={newData.isDefault}
                                onChange={({target: {checked}}) => this.onChangeData("isDefault", checked)}
                                className={styles.checkboxItemRight}
                                disabled={!showDefault}>
                                {t("user:contact.makeDefault")}
                            </Checkbox>
                            <div>
                                <KlassDropdown
                                    onChange={(newValue) => this.onChangeData("userPersonalContactRoleId", newValue.id)}
                                    options={roles}
                                    placeholder={t("user.role")}
                                    value={roleValue}
                                    className={!!errors["userPersonalContactRoleId"] ? styles.errorSelect : ""}
                                    menuPortalTarget={null}
                                />
                            </div>
                        </div>
                    </Col>
                    <Col span={12}>
                        <div className={styles.emergencyWrap}>
                            <p className={styles.bodyItemTitle}>{t("common:message.emergencyContact")}</p>
                            <div className={styles.emergencyBodyWrap}>
                                <p className={styles.emergencyText}>No</p>
                                <Switch
                                    checked={newData.isEmergency}
                                    onChange={(newValue) => this.onChangeData("isEmergency", newValue)}
                                    className={!!errors["isEmergency"] ? styles.error : ""}
                                />
                                <p className={styles.emergencyTextYes}>{t("common:message.yes")}</p>
                            </div>
                        </div>
                    </Col>
                </Row>
                <div className={styles.actionWrap}>
                    <div className={styles.addBtnWrap}>
                        <BaseButton
                            title={newData.id ? t("common:action.save") : t("common:action.add")}
                            onClick={newData.id ? this.onEdit : this.onSubmit}
                            className={styles.addBtn}
                            loading={isSubmitting}
                            disabled={!isChanged}
                        />
                    </div>
                </div>
            </BasePopup>
        )
    }
}
export default withTranslation(["user", "chat"])(AddContactForm)
